import * as React from 'react'
import { CSSProperties, FunctionComponent, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react'
import { Card, Divider, List, makeStyles, mergeClasses, shorthands, Skeleton, SkeletonItem, tokens } from '@fluentui/react-components'
import { Formik } from 'formik'
import BaseOverviewFilter, { BaseOverviewFilterProps, BaseOverviewFilterType, ViewType } from './BaseOverviewFilter'
import { BaseOverviewSortingType } from './BaseSortMenu'
import { CheckboxOnChangeData } from '@fluentui/react-checkbox'
import BaseOverviewPage, { BaseOverviewPageProps } from './BaseOverviewPage'
import { EntityDto } from '../../config/appConfig'
import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../hooks/useViewport'
import useLocalStorage from '../../hooks/useLocalStorage'
import { parseAttributeToParams } from '../../components/Form/Attributes/AttributesList'
import { BaseOverviewColumn } from './BaseOverviewColumn'
import Loading from '../../components/Loading'
import AuthorizationContext from '../../context/AuthorizationContext'

export const useOverviewStyles = makeStyles({
    //Move this container up when all pages have been updated.
    pageContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingHorizontalS,
    },
    columnContainer: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 900px) minmax(0, 1fr)',
    },
    columnContainerXXXL: {
        display: 'grid',
        gridTemplateColumns: '1fr',
    },
    multiColumnContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(var(--baseOverviewColumnCount), 1fr)',
        gap: tokens.spacingHorizontalS,
        '> div': {
            width: `var(--baseOverviewColumnWidth)`,
        },
    },
    filterContainer: {
        display: 'grid',
        gridTemplateColumns: '34px auto minmax(max-content, 34px)',
        gap: tokens.spacingVerticalS,

        paddingLeft: tokens.spacingHorizontalXXL,
        paddingRight: tokens.spacingHorizontalXXL,
    },
    filterContainerOutlookNew: {
        gridTemplateColumns: 'auto',

        ...shorthands.padding(0, tokens.spacingHorizontalS),
        '> span': {
            ...shorthands.padding(0, tokens.spacingHorizontalXS),
        },
    },
    filterInput: {
        ...shorthands.padding(0, tokens.spacingHorizontalXS),
    },
    filtersContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    listBorders: {
        '> div > :not(:last-child)': {
            borderBottom: '1px solid var(--colorNeutralStroke2)',
        },

        '> div > :nth-child(even)': {
            backgroundColor: tokens.colorNeutralBackground3,

            '&:hover': {
                backgroundColor: tokens.colorBrandBackground2Hover,
            },
        },
    },
    list: {
        width: 'min(874px, 100%)',
        alignSelf: 'center',

        '@media screen and (min-width: 930px)': {
            paddingLeft: tokens.spacingHorizontalM,
            paddingRight: tokens.spacingHorizontalM,
            borderLeft: '1px solid var(--colorNeutralStroke2)',
            borderRight: '1px solid var(--colorNeutralStroke2)',
        },
    },
    listXXXL: {
        width: '100%',
        paddingLeft: 'unset',
        paddingRight: 'unset',
        borderLeft: 'unset',
        borderRight: 'unset',
    },
    listPage: {
        minHeight: '800px',
    },
    listItem: {
        display: 'grid',
        gridTemplateColumns: '1fr 5fr 1fr',
        minHeight: '24px',

        gap: tokens.spacingVerticalS,
        padding: tokens.spacingVerticalS,

        '&:hover': {
            backgroundColor: tokens.colorBrandBackground2Hover,
        },
    },
    listHeaderItem: {
        backgroundColor: tokens.colorNeutralBackground5,
    },
    listBaseHeaderItem: {
        display: 'grid',
        gridTemplateRows: '1fr',
        gridTemplateColumns: 'var(--baseOverviewAvatarWidth) auto',
    },
    listBaseCheckboxHeaderItem: {
        display: 'grid',
        gridTemplateColumns: 'var(--baseOverviewCheckboxWidth) var(--baseOverviewAvatarWidth) auto',
        gridTemplateRows: '1fr',
    },
    listItemEmpty: {
        gridTemplateColumns: 'auto',
    },
    skeletonItem: {
        minHeight: '30px',
    },
    gridGapXS: {
        display: 'grid',
        gap: tokens.spacingVerticalXS,
    },
    gridSpan2Columns: {
        gridColumn: 'span 2',
    },
    gridSpan2Rows: {
        gridRow: 'span 2',
    },
    flexGap: {
        display: 'flex',
        gap: tokens.spacingVerticalS,
    },
    flexGapXS: {
        display: 'flex',
        gap: tokens.spacingVerticalXS,
    },
    flexGrow: {
        display: 'flex',
        flexGrow: 1,
    },
    flexShrink: {
        display: 'flex',
        flexGrow: 1,
    },
    flexRow: {
        display: 'flex',
        flexDirection: 'row',
    },
    flexWrap: {
        flexWrap: 'wrap',
    },
    flexColumn: {
        display: 'flex',
        flexDirection: 'column',
    },
    flexJustifyBetween: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    flexJustifyEnd: {
        display: 'flex',
        justifyContent: 'end',
    },
    flexAlignContentStart: {
        flexWrap: 'wrap',
        alignContent: 'start',
    },
    alignEnd: {
        textAlign: 'end',
    },
    centerItems: {
        alignSelf: 'center',
        textAlign: 'center',
        fontSize: tokens.fontSizeBase300,
    },
    alignItemsCenter: {
        alignItems: 'center',
    },
    alignItemsStart: {
        alignItems: 'start',
    },
    alignItemsEnd: {
        alignItems: 'end',
        textAlign: 'end',
    },
    alignSelfStart: {
        alignSelf: 'start',
    },
    hidden: {
        display: 'none',
    },
    invisible: {
        visibility: 'hidden',
    },
    colorRed: {
        color: tokens.colorStatusDangerForeground3,
    },
    colorOrange: {
        color: tokens.colorStatusWarningBackground3,
    },
    colorGreen: {
        color: tokens.colorStatusSuccessBackground3,
    },
    paddingXS: {
        padding: tokens.spacingHorizontalXS,
    },
})

/**
 * Basis for overview page props. As little as possible is required.
 */
export type BaseOverviewProps<
    EntityT extends EntityDto,
    FilterT extends BaseOverviewFilterType,
    SortingT extends BaseOverviewSortingType<any>,
    HeaderT extends BaseOverviewCount = BaseOverviewCount,
    ActionButtonPropsT = any,
    DefaultFilterTagsPropsT = any,
> = PropsWithChildren<{
    defaultFilterValues: FilterT
    fixedFilterValues?: FilterT
    fixedColumnFilterValues?: (viewType: ViewType, values: FilterT) => FilterT[]
    batchSizeItems?: number
    maxCount?: number
    countItems: (column?: number, filterValues?: FilterT) => Promise<HeaderT>
    onCountItems?: (column: number, result: HeaderT) => void | Promise<any>
    loadItems: (column: number, filterValues?: FilterT, sortingValues?: SortingT, start?: number, size?: number) => Promise<EntityT[]>
    onLoadItems?: (column: number, columnPages: EntityT[][][], columnItems: EntityT[]) => void | Promise<any>
    alwaysLoadAllItems?: boolean

    listColumnFullWidth?: boolean // When viewing as a list, make the column 100% width
    listClassnames?: string[]
    listItemHeaderContent?: FunctionComponent<BaseOverviewHeaderItemProps<EntityT, FilterT, SortingT, HeaderT>>
    listItemHeaderClassnames?: string[]
    listItemContent: (props: BaseOverviewLineItemProps<EntityT, FilterT, SortingT>) => React.ReactNode
    listItemClassnames?: (props: Partial<BaseOverviewLineItemProps<EntityT, FilterT, SortingT>>) => string[]

    headerActionsButtons?: FunctionComponent<BaseOverviewProps<EntityT, FilterT, SortingT> & ActionButtonPropsT>
    headerActionsButtonsProps?: ActionButtonPropsT
    headerDefaultFilterTags?: FunctionComponent<BaseOverviewProps<EntityT, FilterT, SortingT> & DefaultFilterTagsPropsT>
    headerDefaultFilterTagsProps?: DefaultFilterTagsPropsT
    headerSortingItems?: SortingT[]
    filterContent?: FunctionComponent<BaseOverviewFilterProps<EntityT, FilterT, SortingT>>
    filterInputHint?: FunctionComponent<BaseOverviewFilterProps<EntityT, FilterT, SortingT>>
    filterFromJson?: (filterValues: FilterT) => FilterT
    filterToJson?: (filterValues: FilterT) => FilterT
    inputPlaceholderKey?: string
    listEmptyPlaceholderKey?: string
    kanbanEmptyPlaceholderKey?: string

    isItemSelectable?: (item: EntityT) => boolean
    isAllItemsSelectable?: (items: EntityT[]) => boolean
    isItemSelected?: (item: EntityT) => boolean
    isAllItemsSelected?: (items: EntityT[]) => boolean
    onItemSelected?: (data: CheckboxOnChangeData, item: EntityT) => void
    onAllItemsSelected?: (data: CheckboxOnChangeData, items: EntityT[]) => void
    selectedItems?: EntityT[]

    onItemClicked?: (props: BaseOverviewLineItemProps<EntityT, FilterT, SortingT>) => void
    reloadTrigger?: any

    localStorageKeyPrefix?: string

    focusQueryOnRender?: boolean
}>

export type BaseOverviewCount = {
    total: number
}

export type BaseOverviewHeaderItemProps<
    EntityT extends EntityDto,
    FilterT extends BaseOverviewFilterType,
    SortingT extends BaseOverviewSortingType<any>,
    HeaderT extends BaseOverviewCount = BaseOverviewCount,
> = BaseOverviewProps<EntityT, FilterT, SortingT, HeaderT> & {
    viewportTiny: boolean
    viewportNarrow: boolean
    viewportLarge: boolean
    viewType: ViewType
    columnIndex: number
    filterValues: FilterT
    count?: HeaderT
    items?: EntityT[]

    isItemSelectable?: (item: EntityT) => boolean
    isAllItemsSelectable?: (items: EntityT[]) => boolean
    isItemSelected?: (item: EntityT) => boolean
    isAllItemsSelected?: (items: EntityT[]) => boolean
    onItemSelected?: (data: CheckboxOnChangeData, item: EntityT) => void
    onAllItemsSelected?: (data: CheckboxOnChangeData, items: EntityT[]) => void
    selectedItems?: EntityT[]
}

export type BaseOverviewLineItemProps<
    EntityT extends EntityDto,
    FilterT extends BaseOverviewFilterType,
    SortingT extends BaseOverviewSortingType<any>,
> = BaseOverviewPageProps<EntityT, FilterT, SortingT> & {
    index: number
    key: number | string
    item: EntityT
}

export function BaseOverviewSkeletonListItem() {
    const styles = useOverviewStyles()

    return (
        <div className={mergeClasses(styles.listItem, styles.skeletonItem)}>
            <SkeletonItem size={24} shape={'circle'} />
            <SkeletonItem size={24} />
            <SkeletonItem size={24} />
        </div>
    )
}

export function parseFilterValuesToParams<FilterT extends BaseOverviewFilterType, SortingT extends BaseOverviewSortingType<any>>(
    filterValues?: FilterT,
    sortingValues?: SortingT,
    start?: number,
    size?: number,
): any {
    return {
        ...filterValues,
        icon: undefined,
        title: undefined,
        tag: undefined,
        start: start,
        size: size,
        sort: sortingValues && parseSortingValuesToParams(sortingValues),
        attributes: filterValues?.['attributes'] && parseAttributeToParams(filterValues?.['attributes']),
    }
}

export function parseSortingValuesToParams<SortingT extends BaseOverviewSortingType<any>>(sortingValues?: SortingT): string[] {
    if (sortingValues && sortingValues?.property) {
        return sortingValues.property.map(property => `${property.toString()}-${sortingValues?.direction?.toLocaleLowerCase()}`)
    }
    return undefined
}

/**
 * Basis of an overview.
 * Implementing components should implement subtypes of BaseOverviewFilterType and BaseOverviewSortingType.
 * @param props see {@link BaseOverviewProps} for more information on which fields to include.
 */
export default function BaseOverview<
    EntityT extends EntityDto,
    FilterT extends BaseOverviewFilterType,
    SortingT extends BaseOverviewSortingType<any>,
    HeaderT extends BaseOverviewCount = BaseOverviewCount,
    ActionButtonPropsT = any,
    DefaultFilterTagsPropsT = any,
>(props: BaseOverviewProps<EntityT, FilterT, SortingT, HeaderT, ActionButtonPropsT, DefaultFilterTagsPropsT>) {
    const styles = useOverviewStyles()
    const { host } = useContext(AuthorizationContext)
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const viewportXXL = useMemo<boolean>(() => isViewport('>=', Viewport.XXL), [viewport])
    const viewportTiny = useMemo<boolean>(() => isViewport('<', Viewport.OutlookWeb), [viewport])
    const viewportNarrow = useMemo<boolean>(() => isViewport('<', Viewport.OutlookNew), [viewport])
    const viewportLarge = useMemo<boolean>(() => isViewport('<', Viewport.Large), [viewport])

    const [filtersOpen, setFiltersOpen] = useState<boolean>(false)

    const {
        loaded: loadedViewType,
        values: viewType,
        setValues: setViewType,
    } = useLocalStorage<ViewType>({
        key: props.localStorageKeyPrefix ? `${props.localStorageKeyPrefix}ViewType` : undefined,
        hostKey: host?.RowKey,
        defaultValue: ViewType.ListView,
        fromJson: values => values,
        toJson: values => values,
        load: !!host,
    })
    const currentViewType: ViewType = useMemo(
        () => (viewportXXL && loadedViewType && viewType) || ViewType.ListView,
        [viewportXXL, loadedViewType, viewType],
    )
    const {
        loaded: loadedFilterValues,
        values: filterValues,
        setValues: setFilterValues,
    } = useLocalStorage<FilterT>({
        key: props.localStorageKeyPrefix ? `${props.localStorageKeyPrefix}Filter` : undefined,
        hostKey: host?.RowKey,
        defaultValue: { ...props.fixedFilterValues, ...props.defaultFilterValues },
        fromJson: values => {
            return values.query?.length >= 3 ? values : props.filterFromJson?.(values) || values
        },
        toJson: values => {
            return values.query?.length >= 3 ? values : props.filterToJson?.(values) || values
        },
        load: !!host,
    })
    const {
        loaded: loadedSortingValues,
        values: sortingValues,
        setValues: setSortingValues,
    } = useLocalStorage<SortingT>({
        key: props.localStorageKeyPrefix ? `${props.localStorageKeyPrefix}Sorting` : undefined,
        hostKey: host?.RowKey,
        defaultValue: props.headerSortingItems?.find(item => !!item.default),
        fromJson: values => {
            const property = Array.isArray(values.property) ? values.property : [values.property].filter(Boolean)
            values.property =
                props.headerSortingItems.find(item => property?.[0] === item.property[0] && item.direction === values.direction)?.property ||
                props.headerSortingItems.find(item => item.default)?.property ||
                property
            return values
        },
        toJson: values => values,
        load: !!host,
    })

    const fixedColumnFilterValues: FilterT[] = useMemo(
        () => (loadedFilterValues && props.fixedColumnFilterValues?.(currentViewType, filterValues)) || ([{}] as FilterT[]),
        [loadedFilterValues, currentViewType, filterValues],
    )

    const batchSizeItems: number = useMemo(() => props.batchSizeItems ?? 20, [[props.batchSizeItems]])
    const [count, setCount] = useState<HeaderT[]>(fixedColumnFilterValues.map(() => ({ total: 0 }) as HeaderT))
    const pages: number[] = useMemo(
        () => count?.map(countItem => Math.ceil((countItem?.total ?? 0) / batchSizeItems)) ?? fixedColumnFilterValues.map(() => 0),
        [count, batchSizeItems],
    )

    const [items, setItems] = useState<EntityT[][][]>([])
    const [loading, setLoading] = useState<boolean>(true)

    const onUpdateCount = (columns?: number[]): Promise<void> => {
        const hasColumns = columns && columns.length > 0
        //Reset count
        if (!hasColumns) {
            setCount(fixedColumnFilterValues.map(() => ({ total: 0 }) as HeaderT))
        } else {
            const newCount = count ?? fixedColumnFilterValues.map(() => ({ total: 0 }) as HeaderT)
            columns.forEach(column => {
                newCount[column] = { total: 0 } as HeaderT
            })
            setCount([...newCount])
        }

        //Invoke recount
        return Promise.all(
            fixedColumnFilterValues.map((columnFilters, columnIndex) => {
                if (!hasColumns || columns.includes(columnIndex)) {
                    return props
                        .countItems(columnIndex, { ...filterValues, ...columnFilters })
                        .then(
                            response =>
                                ({ ...response, total: props.maxCount ? Math.min(props.maxCount, response?.total) : response?.total }) as HeaderT,
                        )
                        .then(result => {
                            const newCount = count ?? (Array(fixedColumnFilterValues.length) as HeaderT[])
                            newCount[columnIndex] = result
                            setCount([...newCount])

                            const newItems = items ?? (Array(fixedColumnFilterValues.length) as EntityT[][][])
                            newItems[columnIndex] = []
                            setItems([...newItems])

                            props.onCountItems?.(columnIndex, result)
                        })
                }
                return Promise.resolve()
            }),
        ).then(() => {})
    }

    const onUpdatePageItems = (column: number, page: number, start?: number, size?: number): Promise<EntityT[]> => {
        return props
            .loadItems(column, { ...filterValues, ...fixedColumnFilterValues[column] }, sortingValues, start, size)
            .then(result => {
                const newItems = items
                newItems[column][page] = result
                setItems([...newItems])
                return setItems([...items])
            })
            .then(() =>
                props.onLoadItems?.(
                    column,
                    items,
                    items
                        .reduce((acc, item) => acc.concat(item), [])
                        .reduce((acc, item) => acc.concat(item), [])
                        .filter(Boolean),
                ),
            )
    }

    useEffect(() => {
        setLoading(true)
        if (loadedViewType && loadedFilterValues && loadedSortingValues) {
            onUpdateCount().then(() => setLoading(false))
        }
    }, [currentViewType, filterValues, sortingValues, props.reloadTrigger])

    if (!loadedViewType || !loadedFilterValues || !loadedSortingValues) {
        return <Loading />
    }

    return (
        <div
            className={styles.pageContainer}
            style={
                {
                    '--baseOverviewColumnCount': `${fixedColumnFilterValues.length || 1}`,
                    '--baseOverviewColumnWidth': `${1 / (fixedColumnFilterValues.length || 1)}fr`,
                    '--baseOverviewCheckboxWidth': `28px`,
                    '--baseOverviewAvatarWidth': `28px`,
                    '--baseOverviewIconWidth': `28px`,
                    '--baseOverviewDoubleIconWidth': `56px`,
                    '--baseOverviewDateWidth': `70px`,
                    '--baseOverviewTimeWidth': `48px`,
                    '--baseOverviewFileWidth': `80px`,
                    '--baseOverviewPriceWidth': `90px`,
                    '--baseOverviewPhoneWidth': `120px`,
                } as CSSProperties
            }
        >
            <Formik<FilterT> initialValues={filterValues} validateOnChange onSubmit={setFilterValues} validate={setFilterValues}>
                {props.filterContent && (
                    <BaseOverviewFilter<EntityT, FilterT, SortingT>
                        {...props}
                        filtersOpen={filtersOpen}
                        setFiltersOpen={(value?: boolean) =>
                            //Use falsy check for booleans, otherwise 'false' will result in a 'true' through '!oldValue'.
                            setFiltersOpen(oldValue => value ?? !oldValue)
                        }
                        sortingValues={sortingValues}
                        setSortingValues={setSortingValues}
                        viewportTiny={viewportTiny}
                        viewportNarrow={viewportNarrow}
                        viewportLarge={viewportLarge}
                        viewType={currentViewType}
                        setViewType={setViewType}
                    >
                        <props.filterContent
                            {...props}
                            filtersOpen={filtersOpen}
                            setFiltersOpen={setFiltersOpen}
                            sortingValues={sortingValues}
                            setSortingValues={setSortingValues}
                            viewportTiny={viewportTiny}
                            viewportNarrow={viewportNarrow}
                            viewportLarge={viewportLarge}
                            viewType={currentViewType}
                            setViewType={setViewType}
                        />
                    </BaseOverviewFilter>
                )}
            </Formik>

            {props.children}

            <Divider className={styles.flexGrow} />

            {loading && (
                <Skeleton
                    className={mergeClasses(
                        ...[styles.listBorders, viewType === ViewType.KanbanView && styles.listXXXL, ...(props.listClassnames || [styles.list])],
                    )}
                >
                    <BaseOverviewSkeletonListItem />
                    <BaseOverviewSkeletonListItem />
                    <BaseOverviewSkeletonListItem />
                    <BaseOverviewSkeletonListItem />
                    <BaseOverviewSkeletonListItem />
                </Skeleton>
            )}
            {!loading && currentViewType && (
                <div
                    className={mergeClasses(
                        styles.columnContainer,
                        props.listColumnFullWidth && styles.columnContainerXXXL,
                        fixedColumnFilterValues.length > 1 && styles.multiColumnContainer,
                    )}
                >
                    {fixedColumnFilterValues.length === 0 && (
                        <Card className={mergeClasses(styles.flexGrow, styles.flexColumn, styles.paddingXS)}>
                            <ul>
                                <li
                                    key={`0--4`}
                                    className={mergeClasses(
                                        ...[
                                            styles.listItem,
                                            ...(props.listItemClassnames?.({
                                                ...props,
                                                viewportNarrow: viewportNarrow,
                                                viewType: currentViewType,
                                                index: -4,
                                                key: `0--4`,
                                            }) || []),
                                            styles.listItemEmpty,
                                        ],
                                    )}
                                >
                                    <div>{translate(props.kanbanEmptyPlaceholderKey ?? 'base.empty.filter')}</div>
                                </li>
                            </ul>
                        </Card>
                    )}
                    {fixedColumnFilterValues.length === 1 && <div></div>}
                    {fixedColumnFilterValues.map((columnFilterValues, columnIndex) => (
                        <BaseOverviewColumn
                            key={columnIndex}
                            {...props}
                            columnFilterValues={columnFilterValues}
                            viewportTiny={viewportTiny}
                            viewportNarrow={viewportNarrow}
                            viewportLarge={viewportLarge}
                            viewType={currentViewType}
                        >
                            <List
                                className={mergeClasses(
                                    ...[
                                        styles.listBorders,
                                        !props.listColumnFullWidth && styles.list,
                                        viewType === ViewType.KanbanView && styles.listXXXL,
                                        ...(props.listClassnames || []),
                                    ],
                                )}
                            >
                                {count[columnIndex] && props.listItemHeaderContent && (
                                    <li
                                        key={`${columnIndex}--2`}
                                        className={mergeClasses(
                                            ...[
                                                styles.listItem,
                                                ...(props.listItemClassnames?.({
                                                    ...props,
                                                    viewportTiny: viewportTiny,
                                                    viewportNarrow: viewportNarrow,
                                                    viewportLarge: viewportLarge,
                                                    viewType: currentViewType,
                                                    index: -2,
                                                    key: `${columnIndex}--2`,
                                                }) || []),
                                                styles.listHeaderItem,
                                                ...(props.listItemHeaderClassnames || []),
                                            ],
                                        )}
                                    >
                                        <props.listItemHeaderContent
                                            key={-2}
                                            {...props}
                                            viewportTiny={viewportTiny}
                                            viewportNarrow={viewportNarrow}
                                            viewportLarge={viewportLarge}
                                            viewType={currentViewType}
                                            columnIndex={columnIndex}
                                            filterValues={filterValues}
                                            count={count[columnIndex]}
                                            items={items[columnIndex].reduce((acc, item) => acc.concat(item), []).filter(Boolean)}
                                        />
                                    </li>
                                )}
                                {count[columnIndex] && count[columnIndex].total === 0 && (
                                    <li
                                        key={`${columnIndex}--3`}
                                        className={mergeClasses(
                                            ...[
                                                styles.listItem,
                                                ...(props.listItemClassnames?.({
                                                    ...props,
                                                    viewportNarrow: viewportNarrow,
                                                    viewType: currentViewType,
                                                    index: -3,
                                                    key: `${columnIndex}--3`,
                                                }) || []),
                                                styles.listItemEmpty,
                                            ],
                                        )}
                                    >
                                        <div>{translate(props.listEmptyPlaceholderKey ?? 'base.empty')}</div>
                                    </li>
                                )}
                                {[...Array(pages[columnIndex]).keys()].map((pageIndex, index) => (
                                    <BaseOverviewPage
                                        key={`${columnIndex}-${index}`}
                                        {...props}
                                        viewportTiny={viewportTiny}
                                        viewportNarrow={viewportNarrow}
                                        viewportLarge={viewportLarge}
                                        viewType={currentViewType}
                                        filterValues={filterValues}
                                        sortingValues={sortingValues}
                                        count={count[columnIndex]}
                                        batchSizeItems={batchSizeItems}
                                        columnIndex={columnIndex}
                                        pageIndex={pageIndex}
                                        items={items[columnIndex]}
                                        onUpdateItem={(item, columns) => onUpdateCount(columns)}
                                        onUpdatePageItems={onUpdatePageItems}
                                    />
                                ))}
                            </List>
                        </BaseOverviewColumn>
                    ))}
                    {fixedColumnFilterValues.length === 1 && <div></div>}
                </div>
            )}
        </div>
    )
}
